import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import directMessageService from '../../services/api/directMessageService';
import { getPublicProfile } from '../../services/api/userService';
import WebSocketContext from '../../contexts/WebSocketContext';
import '../../styles/features/directMessages.css';

// Server-side cap (MS-B09); mirrored here so the user sees it before sending.
export const MAX_DM_LENGTH = 2000;

// Append messages that aren't already in the list (a socket echo and the
// POST response can both deliver the same message).
export function mergeMessages(prev, incoming) {
  const seen = new Set(prev.map((m) => String(m.id)));
  const fresh = incoming.filter((m) => m && !seen.has(String(m.id)));
  return fresh.length ? [...prev, ...fresh] : prev;
}

// Format a timestamp as a short time string
function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Format a date label for the date separator
function fmtDateLabel(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function ConvAvatar({ user }) {
  if (user?.avatar_url) {
    return <img src={user.avatar_url} alt="avatar" className="dm-conv-avatar" />;
  }
  const initial = (user?.first_name?.[0] || user?.username?.[0] || '?').toUpperCase();
  return <div className="dm-conv-avatar-placeholder">{initial}</div>;
}

function DirectMessages({ token, onLogout }) {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(null); // { partner_id, first_name, last_name, username, avatar_url }
  const [newMsg, setNewMsg] = useState('');
  const [convsLoading, setConvsLoading] = useState(true);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [sendError, setSendError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // WebSocket is optional here (no provider in some tests / logged-out shells)
  const ws = useContext(WebSocketContext);
  const activeConvRef = useRef(null);
  const skipScrollRef = useRef(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  // Tracks which partnerId we've already auto-opened — prevents infinite loop
  // when loadMessages updates `conversations` (clears unread badge) and re-triggers this effect
  const openedPartnerRef = useRef(null);

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();

  // True when an id from the URL / API is the signed-in user (id, uuid or short code)
  const isSelfId = (id) =>
    id != null && id !== '' &&
    [currentUser.id, currentUser.uuid, currentUser.short_code, currentUser.shortCode]
      .filter((v) => v != null && v !== '')
      .some((v) => String(v) === String(id));

  // ── Load conversation list ──────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      setConvsLoading(true);
      const res = await directMessageService.getConversations();
      setConversations(res.data?.conversations || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setConvsLoading(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ── Open a conversation by partnerId from URL ───────────────────────────────
  const loadMessages = useCallback(async (uid) => {
    try {
      setMsgsLoading(true);
      setMessages([]);
      setHasMore(false);
      // Newest page of the thread (MS-B06); older pages via loadOlder()
      const res = await directMessageService.getMessages(uid);
      setMessages(res.data?.messages || []);
      setHasMore(Boolean(res.data?.has_more));
      // Clear unread badge in sidebar
      setConversations((prev) =>
        prev.map((c) => (String(c.partner_id) === String(uid) ? { ...c, unread_count: 0 } : c))
      );
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setMsgsLoading(false);
    }
  }, []);

  // ── Load older messages (cursor = oldest id we hold) ───────────────────────
  const loadOlder = async () => {
    if (!activeConv || loadingOlder || messages.length === 0) return;
    try {
      setLoadingOlder(true);
      const res = await directMessageService.getMessages(activeConv.partner_id, { before: messages[0].id });
      const older = res.data?.messages || [];
      skipScrollRef.current = true;
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => String(m.id)));
        return [...older.filter((m) => !seen.has(String(m.id))), ...prev];
      });
      setHasMore(Boolean(res.data?.has_more));
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      setLoadingOlder(false);
    }
  };

  const conversationsRef = useRef([]);
  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  // ── Real-time: messages pushed by the server as `dm:new` (MS-B07) ──────────
  const wsConnected = Boolean(ws?.connected);
  useEffect(() => {
    if (!ws?.addEventListener || !wsConnected) return undefined;
    const handler = (payload) => {
      const msg = payload?.message;
      if (!msg || !msg.id) return;
      const meId = String(currentUser.id);
      const partner = String(msg.sender_id) === meId ? msg.recipient_id : msg.sender_id;
      const open = activeConvRef.current;
      const isOpen = open && String(open.partner_id) === String(partner);
      if (isOpen) {
        setMessages((prev) => mergeMessages(prev, [msg]));
      }
      const known = conversationsRef.current.some((c) => String(c.partner_id) === String(partner));
      if (!known) {
        // A new conversation: refresh the list from the server
        loadConversations();
        return;
      }
      setConversations((prev) => {
        const existing = prev.find((c) => String(c.partner_id) === String(partner));
        if (!existing) return prev;
        const incoming = String(msg.sender_id) !== meId;
        const updated = {
          ...existing,
          last_message: msg.content,
          last_message_at: msg.created_at,
          last_sender_id: msg.sender_id,
          // A muted sender's message arrives but raises no unread badge
          unread_count: incoming && !isOpen && !payload?.muted
            ? (parseInt(existing.unread_count, 10) || 0) + 1
            : existing.unread_count,
        };
        return [updated, ...prev.filter((c) => c !== existing)];
      });
    };
    return ws.addEventListener('dm:new', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, wsConnected, loadConversations]);

  // Reset the opened-partner guard when the URL param changes
  useEffect(() => {
    openedPartnerRef.current = null;
    setSendError('');
  }, [partnerId]);

  // Auto-open conversation when URL has a partnerId
  useEffect(() => {
    if (!partnerId) return;
    // You can't message yourself — /messages/<own id> goes back to the list (BUG-47)
    if (isSelfId(partnerId)) {
      setActiveConv(null);
      setMobileShowChat(false);
      navigate('/messages', { replace: true });
      return;
    }
    // Already opened this partner in this render cycle — skip to avoid loop
    if (openedPartnerRef.current === partnerId) return;

    const existing = conversations.find((c) => String(c.partner_id) === String(partnerId));
    if (existing) {
      openedPartnerRef.current = partnerId;
      setActiveConv(existing);
      loadMessages(existing.partner_id);
      setMobileShowChat(true);
    } else if (!convsLoading) {
      // New conversation — no existing record yet, fetch partner info
      openedPartnerRef.current = partnerId;
      // Use partnerId directly (may be UUID); will be replaced with integer once profile loads
      setActiveConv({ partner_id: partnerId });
      loadMessages(partnerId);
      setMobileShowChat(true);
      // Fetch partner name in background
      getPublicProfile(partnerId)
        .then((res) => {
          // API returns { success: true, data: { user: { ... } } }
          const u = res?.data?.user || res?.data || res || {};
          if (isSelfId(u.id)) {
            setActiveConv(null);
            setMobileShowChat(false);
            navigate('/messages', { replace: true });
            return;
          }
          // Prefer the integer DB id; fall back to whatever was in the URL
          const resolvedId = u.id || partnerId;
          setActiveConv({
            partner_id: resolvedId,
            first_name: u.first_name || '',
            last_name:  u.last_name  || '',
            username:   u.username   || '',
            avatar_url: u.avatar_url || null,
            // Blocked either way: the thread stays readable, nobody can send
            blocked:    u.blocked_by_me === true || u.blocks_me === true,
          });
          // If the URL had a UUID, replace it with the integer so sidebar matching works
          if (u.id && String(u.id) !== String(partnerId)) {
            navigate(`/messages/${u.id}`, { replace: true });
          }
        })
        .catch(() => {/* keep minimal activeConv */});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, conversations, convsLoading, loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    // Prepending older history keeps the reader where they were
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Open a conversation from sidebar ────────────────────────────────────────
  const openConversation = (conv) => {
    setActiveConv(conv);
    loadMessages(conv.partner_id);
    setMobileShowChat(true);
    navigate(`/messages/${conv.partner_id}`, { replace: true });
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ── Send message ─────────────────────────────────────────────────────────────
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv || sending) return;

    const content = newMsg.trim();
    if (content.length > MAX_DM_LENGTH) {
      setSendError(`Message must be at most ${MAX_DM_LENGTH} characters.`);
      return;
    }
    setNewMsg('');
    setSendError('');
    setSending(true);

    try {
      const res = await directMessageService.sendMessage(activeConv.partner_id, content);
      const sent = res.data?.message;
      if (sent) {
        const enriched = {
          ...sent,
          first_name: currentUser.firstName || currentUser.first_name || '',
          last_name:  currentUser.lastName  || currentUser.last_name  || '',
          username:   currentUser.username   || '',
          avatar_url: currentUser.avatar_url || null,
        };
        setMessages((prev) => mergeMessages(prev, [enriched]));

        // Update/promote conversation in sidebar
        setConversations((prev) => {
          const filtered = prev.filter((c) => String(c.partner_id) !== String(activeConv.partner_id));
          return [
            {
              ...activeConv,
              last_message: content,
              last_message_at: new Date().toISOString(),
              last_sender_id: currentUser.id,
              unread_count: 0,
            },
            ...filtered,
          ];
        });
      } else {
        setNewMsg(content);
        setSendError('Message could not be sent. Please try again.');
      }
    } catch (err) {
      setNewMsg(content); // restore on error
      // 403 on a DM send means a block either way
      if (err.response?.status === 403) {
        setActiveConv((c) => (c ? { ...c, blocked: true } : c));
      }
      setSendError(
        err.response?.data?.message || err.response?.data?.error || 'Message could not be sent. Please try again.'
      );
      console.error('Send failed:', err);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────────

  function renderConversationList() {
    if (convsLoading) {
      return <div className="dm-spinner">Loading…</div>;
    }
    if (conversations.length === 0) {
      return (
        <div className="dm-sidebar__empty">
          No messages yet.<br />Open a player profile and hit Message to start a chat.
        </div>
      );
    }
    return conversations.map((conv) => {
      const isActive = String(conv.partner_id) === String(activeConv?.partner_id);
      const displayName = [conv.first_name, conv.last_name].filter(Boolean).join(' ') || conv.username || `User ${conv.partner_id}`;
      const isMe = String(conv.last_sender_id) === String(currentUser.id);
      const preview = conv.last_message
        ? `${isMe ? 'You: ' : ''}${conv.last_message}`
        : 'No messages yet';

      return (
        <button
          key={conv.partner_id}
          className={`dm-conv-item${isActive ? ' dm-conv-item--active' : ''}`}
          onClick={() => openConversation(conv)}
        >
          <ConvAvatar user={conv} />
          <div className="dm-conv-info">
            <div className="dm-conv-name">{displayName}</div>
            <div className="dm-conv-preview">{preview}</div>
          </div>
          <div className="dm-conv-meta">
            <span className="dm-conv-time">{fmtTime(conv.last_message_at)}</span>
            {parseInt(conv.unread_count, 10) > 0 && (
              <span className="dm-conv-badge">{conv.unread_count}</span>
            )}
          </div>
        </button>
      );
    });
  }

  function renderMessages() {
    if (msgsLoading) {
      return <div className="dm-spinner">Loading messages…</div>;
    }
    if (messages.length === 0) {
      return (
        <div className="dm-chat__empty">
          No messages yet. Say hello! 👋
        </div>
      );
    }

    let lastDateLabel = '';
    const olderButton = hasMore ? (
      <div className="dm-load-older" key="__older">
        <button
          type="button"
          className="dm-load-older__btn"
          onClick={loadOlder}
          disabled={loadingOlder}
        >
          {loadingOlder ? 'Loading…' : 'Load older messages'}
        </button>
      </div>
    ) : null;
    const rendered = messages.map((msg) => {
      const isMine = String(msg.sender_id) === String(currentUser.id);
      const dateLabel = fmtDateLabel(msg.created_at);
      const showSep = dateLabel !== lastDateLabel;
      if (showSep) lastDateLabel = dateLabel;

      return (
        <React.Fragment key={msg.id}>
          {showSep && <div className="dm-date-sep">{dateLabel}</div>}
          <div className={`dm-msg dm-msg--${isMine ? 'mine' : 'theirs'}`}>
            <div className="dm-msg__bubble">{msg.content}</div>
            <span className="dm-msg__time">{fmtTime(msg.created_at)}</span>
          </div>
        </React.Fragment>
      );
    });
    return olderButton ? [olderButton, ...rendered] : rendered;
  }

  const partnerName = activeConv
    ? [activeConv.first_name, activeConv.last_name].filter(Boolean).join(' ') || activeConv.username || `User ${activeConv.partner_id}`
    : '';

  return (
    <GameLayout token={token} onLogout={onLogout}>
      <div className="dm-page">
        {/* ── Sidebar ── */}
        <aside className={`dm-sidebar${mobileShowChat ? ' dm-sidebar--hidden' : ''}`}>
          <div className="dm-sidebar__header">
            <h2 className="dm-sidebar__title">💬 Messages</h2>
            {location.key !== 'default' && (
              <button
                className="dm-sidebar__back-btn"
                onClick={() => navigate(-1)}
                title="Go back"
              >
                ← Back
              </button>
            )}
          </div>
          <div className="dm-sidebar__list">
            {renderConversationList()}
          </div>
        </aside>

        {/* ── Chat panel ── */}
        <section className={`dm-chat${mobileShowChat ? ' dm-chat--visible' : ''}`}>
          {activeConv ? (
            <>
              <div className="dm-chat__header">
                <button
                  className="dm-chat__back-btn"
                  aria-label="Back to conversations"
                  onClick={() => { setMobileShowChat(false); navigate('/messages', { replace: true }); }}
                >
                  ←
                </button>
                <div>
                  <p className="dm-chat__partner-name">{partnerName}</p>
                  {activeConv.username && (
                    <p className="dm-chat__partner-username">@{activeConv.username}</p>
                  )}
                </div>
              </div>

              <div className="dm-chat__messages">
                {renderMessages()}
                <div ref={messagesEndRef} />
              </div>

              {sendError && (
                <div className="dm-chat__error" role="alert" data-testid="dm-send-error">
                  {sendError}
                </div>
              )}
              <form className="dm-chat__compose" onSubmit={handleSend}>
                <textarea
                  ref={inputRef}
                  className="dm-chat__input"
                  placeholder={activeConv.blocked ? "You can't message this player" : "Type a message… (Enter to send)"}
                  disabled={activeConv.blocked === true}
                  value={newMsg}
                  onChange={(e) => { setNewMsg(e.target.value); if (sendError) setSendError(''); }}
                  aria-label="Message"
                  onKeyDown={handleKeyDown}
                  rows={1}
                  maxLength={MAX_DM_LENGTH}
                />
                <button
                  type="submit"
                  className="dm-chat__send-btn"
                  disabled={!newMsg.trim() || sending || activeConv.blocked === true}
                >
                  {sending ? '…' : 'Send'}
                </button>
              </form>
            </>
          ) : (
            <div className="dm-chat__no-conv">
              <span className="dm-chat__no-conv-icon">💬</span>
              Select a conversation or visit a player's profile to start messaging.
            </div>
          )}
        </section>
      </div>
    </GameLayout>
  );
}

export default DirectMessages;
